(() => {
  'use strict';

  const RUNTIME_KEY = '__luggexMediaAltSync';
  const DEFAULT_BASE_URL = 'https://luggex-media-alt-sync.invalid/';
  const OBSERVED_ATTRIBUTES = [
    'src',
    'srcset',
    'data-src',
    'data-srcset',
    'data-original',
    'data-original-src',
    'data-lazy-src',
    'data-media-id'
  ];
  const MEDIA_NODE_SELECTOR = 'img,source,[data-media-id],media-gallery,script[data-luggex-media-alt-map]';
  const STARAPPS_SELECTOR = '[id$="-starapps"],[id*="starapps"],[class*="starapps"]';

  const asString = (value) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  };

  function decodePath(pathname) {
    try {
      return decodeURIComponent(pathname);
    }
    catch (_error) {
      return pathname;
    }
  }

  function normalizedPath(pathname) {
    return decodePath(asString(pathname)).replace(/\\+/g, '/').replace(/\/+/g, '/').replace(/\/$/, '');
  }

  function transformedPathVariants(pathname) {
    const path = normalizedPath(pathname);
    if (!path) return [];

    const slash = path.lastIndexOf('/');
    const directory = slash >= 0 ? path.slice(0, slash + 1) : '';
    const filename = slash >= 0 ? path.slice(slash + 1) : path;
    const transformed = filename.match(/^(.*)_\d+x\d*(?:_crop_[a-z-]+)?(\.[^./]+)$/i);

    return transformed
      ? [path, `${directory}${transformed[1]}${transformed[2]}`]
      : [path];
  }

  /**
   * Produce ordered URL keys. Host/path is the strongest identity; a hostless
   * path supports Shopify's CDN proxy; a basename is only a final fallback and
   * is rejected by the index when more than one media item shares it.
   */
  function imageUrlKeys(value, baseHref = DEFAULT_BASE_URL) {
    const raw = asString(value);
    if (!raw || /^(?:data|blob):/i.test(raw)) return [];

    let url;
    try {
      url = new URL(raw, baseHref || DEFAULT_BASE_URL);
    }
    catch (_error) {
      return [];
    }

    if (!/^https?:$/i.test(url.protocol)) return [];

    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    const keys = new Set();
    transformedPathVariants(url.pathname).forEach((path) => {
      const basename = path.slice(path.lastIndexOf('/') + 1);
      if (host) keys.add(`h:${host}|p:${path}`);
      keys.add(`p:${path}`);
      if (basename) keys.add(`b:${basename}`);
    });
    return [...keys];
  }

  function keyRank(key) {
    if (key.startsWith('h:')) return 3;
    if (key.startsWith('p:')) return 2;
    return 1;
  }

  function readAttribute(element, name) {
    return asString(element?.getAttribute?.(name));
  }

  function srcsetUrls(value) {
    return asString(value)
      .split(',')
      .map((candidate) => candidate.trim().split(/\s+/)[0])
      .filter(Boolean);
  }

  function imageUrlCandidates(image) {
    const values = [];
    const add = (value) => {
      if (typeof value === 'string' && value.trim()) values.push(value.trim());
    };

    add(image?.currentSrc);
    add(image?.src);
    ['src', 'srcset', 'data-src', 'data-srcset', 'data-original', 'data-original-src', 'data-lazy-src']
      .forEach((attribute) => {
        const value = readAttribute(image, attribute);
        if (attribute.includes('srcset')) srcsetUrls(value).forEach(add);
        else add(value);
      });

    const picture = image?.parentElement?.tagName === 'PICTURE' ? image.parentElement : null;
    picture?.querySelectorAll?.('source[srcset],source[src]')?.forEach((source) => {
      srcsetUrls(readAttribute(source, 'srcset')).forEach(add);
      add(readAttribute(source, 'src'));
    });

    return [...new Set(values)];
  }

  function validMediaEntry(value) {
    if (!value || typeof value !== 'object') return null;
    const id = asString(value.id);
    if (!id || typeof value.alt !== 'string') return null;

    const urls = Array.isArray(value.urls) ? value.urls : [value.url];
    return {
      id,
      alt: value.alt,
      urls: [...new Set(urls.map(asString).filter(Boolean))]
    };
  }

  function createMediaIndex(data, baseHref = DEFAULT_BASE_URL) {
    const media = Array.isArray(data) ? data : data?.media;
    const index = {
      productId: asString(data?.productId),
      byId: new Map(),
      byKey: new Map()
    };
    if (!Array.isArray(media)) return index;

    media.map(validMediaEntry).filter(Boolean).forEach((entry) => {
      const existing = index.byId.get(entry.id);
      if (existing) {
        // Duplicate server identities are unexpected. Mark them ambiguous so
        // a stale integration response can never overwrite an ALT by guess.
        existing.ambiguous = true;
      }
      else {
        index.byId.set(entry.id, { entry, ambiguous: false });
      }

      entry.urls.forEach((url) => {
        imageUrlKeys(url, baseHref).forEach((key) => {
          const ids = index.byKey.get(key) || new Set();
          ids.add(entry.id);
          index.byKey.set(key, ids);
        });
      });
    });

    return index;
  }

  function entryForId(index, id) {
    const record = index?.byId?.get(asString(id));
    return record && !record.ambiguous ? record.entry : null;
  }

  function resolveMediaByUrl(image, index, baseHref = DEFAULT_BASE_URL) {
    if (!image || !index) return null;

    const hitsByRank = new Map();
    imageUrlCandidates(image).forEach((url) => {
      imageUrlKeys(url, baseHref).forEach((key) => {
        const ids = index.byKey.get(key);
        if (!ids?.size) return;
        const rank = keyRank(key);
        const hits = hitsByRank.get(rank) || new Set();
        ids.forEach((id) => hits.add(id));
        hitsByRank.set(rank, hits);
      });
    });

    if (!hitsByRank.size) return null;
    const strongestRank = Math.max(...hitsByRank.keys());
    const strongestHits = hitsByRank.get(strongestRank);
    if (strongestHits.size !== 1) return null;

    const [id] = strongestHits;
    return entryForId(index, id);
  }

  function closestMediaId(image, gallery) {
    let node = image;
    while (node && node !== gallery) {
      const id = readAttribute(node, 'data-media-id');
      if (id) return id;
      node = node.parentElement;
    }
    return '';
  }

  function resolveMediaForImage(image, index, gallery, baseHref = DEFAULT_BASE_URL) {
    // Integrations can keep a stale data-media-id on a cloned slide while
    // replacing its image URL for the newly selected variant. A unique URL
    // match is the current visual identity and must win that conflict. Only
    // when the URL is unknown or ambiguous do we fall back to the server ID.
    const urlEntry = resolveMediaByUrl(image, index, baseHref);
    return urlEntry || entryForId(index, closestMediaId(image, gallery));
  }

  function parseMapNode(gallery) {
    const mapId = readAttribute(gallery, 'data-luggex-media-alt-map-id');
    let node = mapId ? gallery?.ownerDocument?.getElementById?.(mapId) : null;
    if (!node?.matches?.('script[data-luggex-media-alt-map]')) {
      // Compatibility path for an older preview where the map was nested in
      // the gallery. New markup intentionally keeps it outside the custom
      // element because StarApps scans its direct children.
      node = gallery?.querySelector?.('script[data-luggex-media-alt-map]');
    }
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || node.innerHTML || '{}');
      return data && typeof data === 'object' ? data : null;
    }
    catch (_error) {
      return null;
    }
  }

  function connected(element) {
    return element?.isConnected !== false;
  }

  function relatedStarAppsRoot(root, gallery, productId) {
    if (!root || root === gallery || root.closest?.('media-gallery') === gallery) return true;
    const rootProductId = readAttribute(root, 'data-product-id') || readAttribute(root, 'data-product');
    if (rootProductId && productId && rootProductId === productId) return true;
    return !!productId && asString(root.id).includes(productId);
  }

  function imageRoots(state) {
    const roots = [state.gallery];
    const scope = state.scopeState.scope;
    if (scope?.querySelectorAll && scope !== state.gallery) {
      scope.querySelectorAll(STARAPPS_SELECTOR).forEach((root) => {
        if (relatedStarAppsRoot(root, state.gallery, state.productId)) roots.push(root);
      });
    }
    return [...new Set(roots)];
  }

  function imagesForState(state) {
    const images = new Set();
    imageRoots(state).forEach((root) => {
      root.querySelectorAll?.('img').forEach((image) => images.add(image));
      if (root.matches?.('img')) images.add(root);
    });
    return images;
  }

  function syncState(state) {
    if (state.disposed) return;
    if (!connected(state.gallery) || !connected(state.scopeState.scope)) {
      state.scopeState.unregister(state);
      return;
    }

    // A variant/quick-view HTML swap can replace this JSON script via
    // innerHTML without executing a new script tag. Refresh the scoped index
    // before reconciling so the following image mutation uses fresh server data.
    const data = parseMapNode(state.gallery);
    if (data) {
      state.index = createMediaIndex(data, state.baseHref);
      state.productId = asString(data.productId);
    }

    imagesForState(state).forEach((image) => {
      const entry = resolveMediaForImage(image, state.index, state.gallery, state.baseHref);
      // A valid empty string is intentional: an empty canonical ALT must clear
      // the theme's display fallback, while an unknown entry must be untouched.
      if (!entry || typeof entry.alt !== 'string') return;
      if (image.getAttribute?.('alt') !== entry.alt) image.setAttribute('alt', entry.alt);
    });
  }

  function nodeTouchesMedia(node) {
    if (!node || (node.nodeType !== 1 && node.nodeType !== 11)) return false;
    if (node.matches?.(MEDIA_NODE_SELECTOR)) return true;
    return !!node.querySelector?.(MEDIA_NODE_SELECTOR);
  }

  function relevantMutation(record) {
    if (record.type === 'attributes') {
      if (!OBSERVED_ATTRIBUTES.includes(record.attributeName)) return false;
      const target = record.target;
      return !!target?.matches?.('img,source,[data-media-id],media-gallery') || !!target?.closest?.('img,source,[data-media-id],media-gallery');
    }
    if (record.type === 'childList') {
      if (record.target?.matches?.('media-gallery,[data-media-id],script[data-luggex-media-alt-map]')) return true;
      return [...record.addedNodes, ...record.removedNodes].some(nodeTouchesMedia);
    }
    return false;
  }

  function createScopeState(scope, root, allScopes, registerRoot, disposeRoot) {
    const state = {
      scope,
      galleries: new Set(),
      observer: null,
      scheduled: null,
      schedule: null,
      unregister: null,
      active: true
    };

    const run = () => {
      state.scheduled = null;
      [...state.galleries].forEach((galleryState) => syncState(galleryState));
    };

    state.schedule = () => {
      if (state.scheduled !== null) return;
      const requestFrame = root.requestAnimationFrame?.bind(root) || ((callback) => root.setTimeout(callback, 0));
      state.scheduled = requestFrame(run);
    };

    state.unregister = (galleryState) => {
      state.galleries.delete(galleryState);
      galleryState.disposed = true;
      if (state.galleries.size > 0) return;

      state.active = false;
      state.observer?.disconnect();
      if (state.scheduled !== null) {
        if (root.cancelAnimationFrame) root.cancelAnimationFrame(state.scheduled);
        else root.clearTimeout?.(state.scheduled);
        state.scheduled = null;
      }
      allScopes.delete(state);
    };

    if (root.MutationObserver) {
      state.observer = new root.MutationObserver((records) => {
        records.forEach((record) => {
          if (record.type !== 'childList') return;
          record.addedNodes.forEach((node) => registerRoot?.(node));
          record.removedNodes.forEach((node) => disposeRoot?.(node));
        });
        if (records.some(relevantMutation)) state.schedule();
      });
      state.observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: OBSERVED_ATTRIBUTES
      });
    }

    allScopes.add(state);
    return state;
  }

  function createBrowserRuntime(root) {
    const document = root.document;
    const galleryStates = new WeakMap();
    const scopeStates = new WeakMap();
    const allScopes = new Set();
    let registerRoot;
    let disposeRoot;

    const findScope = (gallery) => gallery.closest?.('[id^="shopify-section-"]') ||
      gallery.closest?.('.quick-view__content') ||
      gallery.closest?.('.product') ||
      gallery;

    const getScopeState = (scope) => {
      let state = scopeStates.get(scope);
      if (!state || !state.active) {
        state = createScopeState(scope, root, allScopes, registerRoot, disposeRoot);
        scopeStates.set(scope, state);
      }
      return state;
    };

    const registerGallery = (gallery) => {
      if (!gallery || gallery.nodeType !== 1) return null;
      const data = parseMapNode(gallery);
      if (!data) return null;

      const existing = galleryStates.get(gallery);
      if (existing) {
        existing.index = createMediaIndex(data, document.baseURI || DEFAULT_BASE_URL);
        existing.productId = asString(data.productId);
        existing.baseHref = document.baseURI || DEFAULT_BASE_URL;
        existing.scopeState.schedule();
        return existing;
      }

      const scope = findScope(gallery);
      const scopeState = getScopeState(scope);
      const state = {
        gallery,
        scopeState,
        index: createMediaIndex(data, document.baseURI || DEFAULT_BASE_URL),
        productId: asString(data.productId),
        baseHref: document.baseURI || DEFAULT_BASE_URL,
        disposed: false
      };
      galleryStates.set(gallery, state);
      scopeState.galleries.add(state);
      scopeState.schedule();
      return state;
    };

    registerRoot = (node) => {
      if (!node) return;
      if (node.matches?.('media-gallery')) registerGallery(node);
      node.querySelectorAll?.('media-gallery').forEach(registerGallery);
    };

    disposeRoot = (node) => {
      if (!node) return;
      [...allScopes].forEach((scopeState) => {
        const removals = new Set();
        if (scopeState.scope === node || node.contains?.(scopeState.scope)) {
          scopeState.galleries.forEach((galleryState) => removals.add(galleryState));
        } else {
          scopeState.galleries.forEach((galleryState) => {
            if (galleryState.gallery === node || node.contains?.(galleryState.gallery)) removals.add(galleryState);
          });
        }
        removals.forEach((galleryState) => scopeState.unregister(galleryState));
      });
    };

    // StarApps may replace the whole product section after this runtime has
    // attached its scoped observer. Keep a topology-only observer on the
    // document root so newly inserted/replaced media galleries are registered;
    // attribute reconciliation remains scoped to each registered product area.
    if (root.MutationObserver && document.documentElement) {
      const topologyObserver = new root.MutationObserver((records) => {
        records.forEach((record) => {
          if (record.type !== 'childList') return;
          record.addedNodes.forEach((node) => {
            if (nodeTouchesMedia(node)) registerRoot?.(node);
          });
          record.removedNodes.forEach((node) => {
            if (nodeTouchesMedia(node)) disposeRoot?.(node);
          });
        });
      });
      topologyObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    const scheduleVariantGalleries = (event) => {
      const formId = event.target?.id || asString(event.detail?.formId);
      [...allScopes].forEach((scopeState) => {
        const matches = !formId || [...scopeState.galleries].some((galleryState) => {
          return !galleryState.gallery.getAttribute('form') || galleryState.gallery.getAttribute('form') === formId;
        });
        if (matches) scopeState.schedule();
      });
    };

    document.addEventListener('variant:change', scheduleVariantGalleries, true);
    document.addEventListener('shopify:section:load', (event) => registerRoot(event.target), true);
    document.addEventListener('quickview:loaded', (event) => registerRoot(event.detail?.root || event.target), true);
    document.addEventListener('quickview:open', (event) => registerRoot(event.detail?.root || event.target), true);
    document.addEventListener('shopify:section:unload', (event) => disposeRoot(event.target), true);

    return {
      registerGallery,
      scan: registerRoot,
      dispose: disposeRoot,
      api: {
        imageUrlKeys,
        createMediaIndex,
        resolveMediaByUrl,
        resolveMediaForImage
      }
    };
  }

  const exported = {
    imageUrlKeys,
    createMediaIndex,
    resolveMediaByUrl,
    resolveMediaForImage,
    parseMapNode
  };

  if (typeof module === 'object' && module.exports) module.exports = exported;

  const root = typeof window !== 'undefined'
    ? window
    : (typeof globalThis !== 'undefined' ? globalThis : null);

  if (!root?.document) return;

  const runtime = root[RUNTIME_KEY] || createBrowserRuntime(root);
  root[RUNTIME_KEY] = runtime;

  const scan = () => runtime.scan(root.document);
  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', scan, { once: true });
  }
  scan();
})();
